import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchEquipmentTypes } from '../data-provider';
import type { EquipmentType, EquipmentTypeFilters, EquipmentTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal equipment type lookup cache used to resolve equipment type references
 * without repeatedly requesting the same equipment type data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const equipmentTypesQueryOptions = (
  filters: Partial<EquipmentTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const equipmentTypes = await fetchEquipmentTypes(filters, { signal });
      put(equipmentTypes.list);
      return equipmentTypes;
    },
    queryKey: CACHE_KEYS.equipmentTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_EQUIPMENT_TYPES_CACHE_KEY = btoa('internal-equipment-types-cache-key');

const INTERNAL_QUERY_EQUIPMENT_TYPES_KEY = equipmentTypesQueryOptions({
  description: INTERNAL_EQUIPMENT_TYPES_CACHE_KEY,
}).queryKey;

const getEquipmentTypeFallback = (equipmentTypeId: string): EquipmentType => {
  logger.warn(`Equipment type "${equipmentTypeId}" was not found. Verify the data model.`);
  return {
    description: equipmentTypeId,
    id: equipmentTypeId,
    label: equipmentTypeId,
  };
};

export const put = async (equipmentTypes: EquipmentType[]) => {
  // Sync with storage (currently React Query; may use IndexedDB in the future).
  const data = queryClient.getQueryData(INTERNAL_QUERY_EQUIPMENT_TYPES_KEY);

  const newEquipmentTypes = equipmentTypes.reduce(
    (acc, equipmentType) => {
      acc[String(equipmentType.id)] = equipmentType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_EQUIPMENT_TYPES_KEY, {
    list: Object.values(newEquipmentTypes),
    map: newEquipmentTypes,
  });
};

export const getAll = (): EquipmentTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_EQUIPMENT_TYPES_KEY);
};

export const find = async (equipmentTypeId: string): Promise<EquipmentType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_EQUIPMENT_TYPES_KEY);

  const equipmentType = data?.map[equipmentTypeId];
  if (equipmentType) {
    return equipmentType;
  }

  try {
    const newData = await queryClient.query(equipmentTypesQueryOptions({ search: { id: equipmentTypeId } }));
    if (newData.map[equipmentTypeId]) {
      void put(newData.list);
      return newData.map[equipmentTypeId];
    }
  } catch {
    return getEquipmentTypeFallback(equipmentTypeId);
  }

  return getEquipmentTypeFallback(equipmentTypeId);
};

export const findAll = async (equipmentTypeIds: string[]): Promise<EquipmentType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_EQUIPMENT_TYPES_KEY);

  const missingIds: string[] = [];
  const equipmentTypes: EquipmentType[] = [];

  for (const equipmentTypeId of equipmentTypeIds) {
    const e = data?.map[equipmentTypeId];
    if (e) {
      equipmentTypes.push(e);
    } else {
      missingIds.push(equipmentTypeId);
    }
  }

  if (!missingIds.length) {
    return equipmentTypes;
  }

  try {
    const newData = await queryClient.query(equipmentTypesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const e = newData.map[missingId];
      if (e) {
        equipmentTypes.push(e);
      } else {
        equipmentTypes.push(getEquipmentTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      equipmentTypes.push(getEquipmentTypeFallback(missingId));
    }
  }

  return equipmentTypes;
};
